import styled from 'styled-components';

type BgType = { background: string }
type TextType = { color: string }
type BorderCardType = { background: string; border: string }
type BadgeType = { background: string; color: string }

/* ── Page ── */

export const Container = styled.div<BgType>`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100vh;
  width: 100%;
  background: ${props => props.background};
  padding: 16px 32px;
  box-sizing: border-box;
  overflow-y: auto;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
`;

export const PageTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PageTitle = styled.h1<TextType>`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.color};
  margin: 0;
`;

export const PageSubtitle = styled.p<TextType>`
  font-size: 14px;
  color: ${props => props.color};
  margin: 0;
`;

export const MyExtensionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

export const MyExtensionsLabel = styled.span<TextType>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.color};
`;

export const ActiveBadge = styled.span<BadgeType & { clickable?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.color};
  background: ${props => props.background};
  border-radius: 20px;
  padding: 3px 10px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  user-select: none;
  transition: opacity 0.15s ease;

  &:hover {
    ${props => props.clickable && 'opacity: 0.75;'}
  }
`;

/* ── Grid ── */

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Card ── */

export const CardWrapper = styled.div<BorderCardType>`
  display: flex;
  flex-direction: column;
  background: ${props => props.background};
  border: 1px solid ${props => props.border};
  border-radius: 20px;
  padding: 24px;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const CardIconWrapper = styled.div<BadgeType>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: ${props => props.background};
  color: ${props => props.color};
  flex-shrink: 0;

  svg {
    fill: currentColor;
    width: 26px;
    height: 26px;
  }
`;

export const CardBadge = styled.span<BadgeType>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: ${props => props.color};
  background: ${props => props.background};
  border-radius: 20px;
  padding: 4px 10px;
`;

export const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const CardNameRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

export const CardPrice = styled.span<TextType>`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.color};
  white-space: nowrap;
`;

export const CardName = styled.h3<TextType>`
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.color};
  margin: 0;
`;

export const CardDescription = styled.p<TextType>`
  font-size: 13px;
  color: ${props => props.color};
  margin: 0;
  line-height: 1.55;
`;

export const CardAction = styled.div`
  margin-top: auto;
`;

export const CardDescriptionRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const SaibaMaisLink = styled.a<TextType>`
  font-size: 12px;
  color: ${props => props.color};
  text-decoration: underline;
  white-space: nowrap;
  cursor: pointer;

  &:hover { opacity: 0.75; }
`;

export const CardDivider = styled.div<{ color: string }>`
  height: 1px;
  background: ${props => props.color};
  opacity: 0.5;
`;

export const StoreLink = styled.a<BadgeType>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.color};
  background: ${props => props.background};
  border-radius: 8px;
  padding: 3px 8px;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.15s ease;

  &:hover { opacity: 0.75; }
`;

/* ── Benefits ── */

export const BenefitList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const BenefitText = styled.span<TextType>`
  font-size: 12px;
  color: ${props => props.color};
`;

/* ── Trial subcard ── */

export const TrialSubcard = styled.div<BorderCardType>`
  display: flex;
  flex-direction: column;
  background: ${props => props.background};
  border: 1px solid ${props => props.border};
  border-radius: 14px;
  padding: 8px;
  margin-bottom: 16px;
`;

export const TrialTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const TrialTextGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TrialIconWrapper = styled.div<BadgeType>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  border-radius: 8px;
  flex-shrink: 0;
`;

export const TrialTitle = styled.h4<TextType>`
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.color};
  margin: 0;
`;

export const TrialDescription = styled.p<TextType>`
  font-size: 10px;
  color: ${props => props.color};
  margin: 0;
  line-height: 1.5;
`;

export const TrialButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TrialNote = styled.span<TextType>`
  font-size: 11px;
  color: ${props => props.color};
  text-align: center;
`;

export const SecondaryButton = styled.button<TextType & { border: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${props => props.border};
  background: transparent;
  color: ${props => props.color};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: filter 0.15s ease;

  &:hover { filter: brightness(80%); }
`;

/* ── Subscription warning banner ── */

export const SubscriptionWarningBanner = styled.div<{ background: string; border: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.background};
  border: 1px solid ${props => props.border};
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

export const SubscriptionWarningText = styled.span<TextType>`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.color};
`;

/* ── Highlight section ── */

export const HighlightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const HighlightSectionTitle = styled.h2<TextType>`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.color};
  margin: 0;
`;

export const HighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const HighlightCardWrapper = styled.div<BorderCardType>`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${props => props.background};
  border: 1px solid ${props => props.border};
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
`;

export const HighlightIconWrapper = styled.div<BadgeType>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${props => props.background};
  color: ${props => props.color};
  flex-shrink: 0;

  svg {
    fill: currentColor;
    width: 18px;
    height: 18px;
  }
`;

export const HighlightTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const HighlightTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HighlightBadge = styled.span<BadgeType>`
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.color};
  background: ${props => props.background};
  border-radius: 20px;
  padding: 3px 8px;
  white-space: nowrap;
`;

export const HighlightTitle = styled.h4<TextType>`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.color};
  margin: 0;
`;

export const HighlightSubtitle = styled.p<TextType>`
  font-size: 12px;
  color: ${props => props.color};
  margin: 0;
  line-height: 1.5;
`;
